/* Aide enseignant (/aide). Chaque réponse est vérifiée dans le produit, jamais
   supposée : les phrases entre guillemets sont les chaînes que l'utilisateur lit
   réellement à l'écran (packages/domain/src/assignment/errors.ts, les libellés de
   content-library.tsx et send-assignment-modal.tsx). Une entrée dont la réponse
   n'est pas vérifiable dans le code n'entre pas ici — voir docs/aide-enseignant.md
   pour ce qui a été écarté et pourquoi. */

import { useMemo, useState } from 'react';
import { Bug, CaretRight, MagnifyingGlass } from '@phosphor-icons/react';
import { HeaderContent, HEADER_TITLE_CLASS } from '../components/shell/app-header';

interface Entry {
  q: string;
  terms: string;
  a: React.ReactNode;
}

interface Section {
  title: string;
  entries: Entry[];
}

const SECTIONS: Section[] = [
  {
    title: 'Trouver le bon mot',
    entries: [
      {
        q: 'Je cherche « contrôle », « interro », « fiche »… et je ne trouve rien',
        terms: 'controle interro test quiz fiche feuille partie bloc vocabulaire mot nom introuvable cherche trouve pas',
        a: (
          <>
            <p className="m-0">
              Les écrans ne connaissent que trois genres de documents : <strong>Cours</strong>,{' '}
              <strong>Exercices</strong> et <strong>Évaluation</strong>. Quand vous ne trouvez pas
              quelque chose, c'est presque toujours que vous cherchez un autre mot que celui des
              boutons.
            </p>
            <div className="mt-3 -mx-1 overflow-x-auto px-1">
              <table className="w-full border-collapse text-[13.5px]">
              <thead>
                <tr>
                  <th className="border-b border-border py-1.5 pr-3 text-left font-bold">Vous dites</th>
                  <th className="border-b border-border py-1.5 text-left font-bold">L'application dit</th>
                </tr>
              </thead>
              <tbody className="text-muted-foreground">
                <tr>
                  <td className="py-1.5 pr-3">contrôle, interro, test, quiz</td>
                  <td className="py-1.5 font-semibold text-foreground">Évaluation</td>
                </tr>
                <tr>
                  <td className="py-1.5 pr-3">fiche, feuille d'exercices</td>
                  <td className="py-1.5 font-semibold text-foreground">Exercices</td>
                </tr>
                <tr>
                  <td className="py-1.5 pr-3">partie, bloc</td>
                  <td className="py-1.5 font-semibold text-foreground">Section</td>
                </tr>
                <tr>
                  <td className="py-1.5 pr-3">devoir maison</td>
                  <td className="py-1.5 font-semibold text-foreground">un exercice, puis un envoi</td>
                </tr>
              </tbody>
              </table>
            </div>
            <p className="m-0 mt-3">
              Vos titres, eux, restent libres : « Contrôle — chapitre 3 » est un titre parfaitement
              valable pour une évaluation.
            </p>
          </>
        ),
      },
    ],
  },
  {
    title: 'Envoyer un devoir',
    entries: [
      {
        q: 'Où est le bouton « créer un devoir » ?',
        terms: 'creer devoir nouveau bouton introuvable donner travail maison dm',
        a: (
          <>
            <p className="m-0">
              Il n'existe pas, et c'est voulu : <strong>un devoir n'est pas un document</strong>,
              c'est l'envoi d'un exercice qui existe déjà dans votre bibliothèque.
            </p>
            <p className="m-0 mt-3">
              Le chemin : <strong>Ma bibliothèque</strong> → le menu <strong>⋯</strong> de l'exercice
              → <strong>Envoyer en devoir</strong>. Depuis l'éditeur d'un exercice, c'est le bouton{' '}
              <strong>Envoyer</strong>.
            </p>
          </>
        ),
      },
      {
        q: "Mon document n'a pas de bouton « Envoyer »",
        terms: 'envoyer bouton absent manque seules fiches exercices peuvent etre envoyees en devoir cours evaluation',
        a: (
          <>
            <p className="m-0">
              Le document ouvert est un <strong>cours</strong> ou une <strong>évaluation</strong>.
              Seul un <strong>exercice</strong> s'envoie en devoir — le bouton n'apparaît que là.
              Si vous forcez le passage, l'application répond «&nbsp;Seules les fiches d'exercices
              peuvent être envoyées en devoir&nbsp;».
            </p>
            <p className="m-0 mt-3">
              Une <strong>évaluation</strong> ne s'envoie pas : elle se compose en{' '}
              <strong>examen sur table</strong>. Un <strong>cours</strong> ne s'envoie pas non plus,
              c'est votre support de séance.
            </p>
          </>
        ),
      },
      {
        q: '« Aucune classe ne vous est affectée dans cette école »',
        terms: 'aucune classe affectee ecole vide liste classes absente rattachement direction',
        a: (
          <p className="m-0">
            Votre direction ne vous a pas encore rattaché à une classe. Il n'y a rien à corriger de
            votre côté : demandez le rattachement à votre direction, les classes apparaîtront
            ensuite dans la fenêtre d'envoi.
          </p>
        ),
      },
      {
        q: '« Aucun élève actif dans les classes sélectionnées »',
        terms: 'aucun eleve actif classes selectionnees zero envoi impossible',
        a: (
          <p className="m-0">
            La classe cochée ne compte aucun élève actif — la fenêtre d'envoi l'annonce avant
            l'envoi, en rouge, sous la forme «&nbsp;· 0 élève actif&nbsp;» à côté du nom de la
            classe. L'envoi reste impossible tant qu'aucun élève n'y est actif ; c'est votre
            direction qui inscrit les élèves.
          </p>
        ),
      },
      {
        q: '« Document archivé — envoi impossible »',
        terms: 'document archive envoi impossible corbeille restaurer',
        a: (
          <p className="m-0">
            Le document est dans la corbeille. Restaurez-le depuis{' '}
            <strong>Ma bibliothèque → Corbeille</strong>, puis relancez l'envoi.
          </p>
        ),
      },
      {
        q: "J'envoie le même exercice à trois classes : cela fait-il trois devoirs ?",
        terms: 'plusieurs classes trois devoirs multiple envoi groupe suivi',
        a: (
          <p className="m-0">
            <strong>Non — un seul devoir</strong>, qui porte les classes que vous avez cochées. Vous
            le suivez sur une seule fiche, avec tous les élèves concernés. Pour des dates de remise
            différentes selon la classe, faites en revanche un envoi par classe.
          </p>
        ),
      },
      {
        q: 'La date de remise est-elle obligatoire ?',
        terms: 'date remise obligatoire optionnelle echeance deadline sans date',
        a: (
          <p className="m-0">
            Non. Le champ porte la mention <strong>« (optionnel) »</strong> : un devoir peut partir
            sans date de remise.
          </p>
        ),
      },
      {
        q: 'Quelle différence entre « Rendu photo » et « Parcours interactif » ?',
        terms: 'rendu photo parcours interactif format difference choisir closed',
        a: (
          <>
            <p className="m-0">
              <strong>📷 Rendu photo</strong> — l'élève travaille sur papier et photographie son
              travail. Vous ouvrez ses photos et un bouton <strong>Correction</strong> apparaît sur
              chaque rendu.
            </p>
            <p className="m-0 mt-3">
              <strong>✨ Parcours interactif</strong> — l'élève répond dans l'application. Vous lisez
              ses résultats directement sur la fiche du devoir, sans photo ni étape de correction.
            </p>
          </>
        ),
      },
      {
        q: '« ⚠️ N sections notées sans barème »',
        terms: 'bareme manquant sections notees avertissement correction automatique points',
        a: (
          <p className="m-0">
            C'est un avertissement, pas un blocage : vous pouvez envoyer tel quel. Il signale
            seulement que la correction automatique ne pourra pas se servir de ces sections. Pour
            l'éviter, donnez leurs points à ces sections dans l'éditeur avant d'envoyer.
          </p>
        ),
      },
    ],
  },
  {
    title: 'Examens sur table',
    entries: [
      {
        q: 'Examens sur table ne me propose aucun document',
        terms: 'examen table aucune session aucun document liste vide evaluation creer',
        a: (
          <p className="m-0">
            La liste ne retient que les documents de genre <strong>Évaluation</strong> qui ne sont{' '}
            <strong>pas archivés</strong>. Si elle est vide : soit vous n'avez pas encore créé
            d'évaluation — créez-la dans Ma bibliothèque —, soit la vôtre est à la corbeille et il
            faut la restaurer.
          </p>
        ),
      },
    ],
  },
  {
    title: 'Ma bibliothèque',
    entries: [
      {
        q: 'Mon document a disparu',
        terms: 'document disparu perdu supprime introuvable corbeille archive restaurer',
        a: (
          <p className="m-0">
            Il est dans la <strong>Corbeille</strong>, tout en bas de Ma bibliothèque. Archiver ne
            supprime jamais : la fenêtre de confirmation le dit elle-même, «&nbsp;vous pourrez le
            restaurer à tout moment&nbsp;».
          </p>
        ),
      },
    ],
  },
  {
    title: 'L’assistant Naïa',
    entries: [
      {
        q: "Quels fichiers puis-je joindre à l'Assistant programme ?",
        terms: 'fichier joindre piece jointe pdf image photo word powerpoint docx taille limite mo format',
        a: (
          <>
            <p className="m-0">
              Deux boutons, donc deux familles : une <strong>image</strong> (JPEG, PNG, HEIC, WebP)
              ou un <strong>PDF</strong>. La limite est de <strong>10 Mo par fichier</strong>.
            </p>
            <p className="m-0 mt-3">
              Les fichiers <strong>Word et PowerPoint ne sont pas proposés</strong> par l'Assistant
              programme. Pour un document bureautique, exportez-le en PDF avant de le joindre.
            </p>
          </>
        ),
      },
      {
        q: 'Est-ce que Naïa invente ?',
        terms: 'invente faux erreur fiable confiance verifier hallucination ia',
        a: (
          <>
            <p className="m-0">
              Les réponses sont produites par une IA, et l'application le rappelle sous le champ de
              saisie : «&nbsp;Réponses générées par une IA — vérifiez les informations
              importantes&nbsp;». Relisez donc ce qui compte, comme vous relirez un manuel.
            </p>
            <p className="m-0 mt-3">
              En revanche, <strong>rien ne part chez un élève sans votre geste</strong> : une
              production de Naïa reste dans votre espace tant que vous ne l'avez pas envoyée en
              devoir.
            </p>
          </>
        ),
      },
      {
        q: 'Vais-je perdre ma conversation si je ferme la page ?',
        terms: 'perdre conversation fermer historique retrouver recherche conserve',
        a: (
          <p className="m-0">
            Non. Vos échanges sont conservés et se retrouvent dans{' '}
            <strong>Mes conversations</strong>, qui offre en plus une recherche dans le contenu de
            vos discussions. Vous pouvez fermer et reprendre le fil un autre jour.
          </p>
        ),
      },
    ],
  },
];

const normalize = (s: string) =>
  s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export default function Aide() {
  const [query, setQuery] = useState('');

  const results = useMemo(() => {
    const q = normalize(query.trim());
    if (q.length < 2) return SECTIONS;
    const words = q.split(/\s+/);
    return SECTIONS.map((s) => ({
      ...s,
      entries: s.entries.filter((e) => {
        const hay = normalize(`${e.q} ${e.terms} ${s.title}`);
        return words.every((w) => hay.includes(w));
      }),
    })).filter((s) => s.entries.length > 0);
  }, [query]);

  const empty = results.length === 0;

  return (
    <>
      <HeaderContent width="md" showBurger>
        <div className="min-w-0">
          <h1 className={HEADER_TITLE_CLASS}>Aide</h1>
          <p className="m-0 mt-0.5 text-[13px] sm:text-[13.5px] leading-snug text-muted-foreground">
            Les questions qui reviennent le plus souvent, et ce que l'application répond.
          </p>
        </div>
      </HeaderContent>

      <main className="max-w-[720px] mx-auto w-full px-5 pt-4 pb-[calc(4rem+env(safe-area-inset-bottom))]">
        <div className="relative mt-[18px]">
          <MagnifyingGlass
            size={18}
            className="pointer-events-none absolute left-[14px] top-1/2 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Votre question, ou le message affiché…"
            aria-label="Rechercher dans l’aide"
            className="w-full min-h-[46px] rounded-sm border border-border bg-card py-3 pl-[42px] pr-[14px] font-[inherit] text-[15px] text-foreground"
          />
        </div>

        {empty ? (
          <p className="py-6 text-muted-foreground">
            Aucune réponse ne correspond à « {query.trim()} ». Le bouton{' '}
            <strong>Signaler un bug</strong>, en bas du menu de gauche, nous transmet votre question.
          </p>
        ) : (
          results.map((section) => (
            <section key={section.title} className="mt-8">
              <h2 className="m-0 mb-3 text-[11px] font-extrabold uppercase tracking-[0.08em] text-muted-foreground">
                {section.title}
              </h2>
              <div className="overflow-hidden rounded-card border border-border bg-card shadow-card">
                {section.entries.map((entry) => (
                  <details key={entry.q} className="group border-b border-border last:border-b-0">
                    <summary className="flex min-h-[52px] cursor-pointer list-none items-center gap-3 px-[14px] py-3 text-[15px] font-semibold text-foreground [&::-webkit-details-marker]:hidden">
                      <CaretRight
                        size={15}
                        weight="bold"
                        className="shrink-0 text-muted-foreground transition-transform group-open:rotate-90 motion-reduce:transition-none"
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1">{entry.q}</span>
                    </summary>
                    <div className="px-[14px] pb-4 text-[14px] leading-relaxed text-muted-foreground sm:pl-[42px]">
                      {entry.a}
                    </div>
                  </details>
                ))}
              </div>
            </section>
          ))
        )}

        <div className="mt-10 flex items-start gap-3 rounded-card border border-border bg-naia-info-bg px-[14px] py-4">
          <Bug size={20} weight="duotone" className="mt-0.5 shrink-0 text-naia-blue" aria-hidden />
          <p className="m-0 text-[13.5px] leading-relaxed text-foreground">
            Votre question n'est pas ici, ou quelque chose ne fonctionne pas ? Utilisez{' '}
            <strong>Signaler un bug</strong>, en bas du menu de gauche : décrivez ce que vous
            faisiez et ce que vous attendiez.
          </p>
        </div>
      </main>
    </>
  );
}
